/**
 * useBaseWiring.js
 * Hook factory for creating wiring hooks (Socket, Lighting, Fixture, Machine, Bell)
 *
 * Reduces ~800 lines of duplicate code by providing common functionality:
 * - Panel exit management
 * - Snap point handling
 * - Perpendicular exit logic
 * - Wire marker generation
 * - Local undo/redo during wire drawing
 * - Scene cleanup
 * - Common event handlers
 *
 * Usage:
 * ```js
 * const useSocketWiring = (scene, camera, renderer, snapPoints, options) => {
 *   return useBaseWiring({
 *     scene, camera, renderer, snapPoints, options,
 *     config: {
 *       commandType: COMMANDS.DRAW_SOCKET,
 *       wireColor: 0x00ff00,
 *       itemTypes: socketTypes,
 *       itemTypeName: 'socket',
 *       createSymbol: (position, direction, angle, isPreview, itemType) => { ... },
 *       createCommand: (action, componentData, feederData, sceneData) => { ... }
 *     }
 *   });
 * };
 * ```
 */

import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import { resetOperation, setCommandMessage } from '../../../redux/operationSlice';
import { clearSelection } from '../../../redux/selectionSlice';
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';
import { getSnappedPoint, getSnappedPointForPanel } from '../../../shared/snap/getSnappedPoint';
import { applyGridSnap, getOrthoSnappedPoint } from '../../../modules/drawing';
import { calculatePerpendicularExitPoint } from '../utils/calculatePerpendicularExit';
import {
    updatePanelExitPoints,
    calculatePanelExitCount,
    findPanelExitPoints,
    updatePanelTitles,
    removeLastPanelExit,
    savePanelExitState,
    restorePanelExitState,
    updatePanelInputWire,
    updatePanelWiringPositions
} from '../utils/panelExitManager';
import { handleSnapWall } from '../utils/snapWallHandler';
import { selectAllFloorPlans } from '../../../redux/floorPlanSlice';
import { selectAllWires } from '../../../redux/wireSlice';
import { WireMarkerGenerator } from '../utils/WireMarkerGenerator';
import { fixtureLineModeRef } from '../utils/fixtureLineMode';
import { SceneCleanupManager } from '../../../utils/scene/SceneCleanupManager';
import { getObjectById } from '../../../utils/getObjectById';
import { getPerpendicularIntersection } from '../../../shared/geometry/getPrependicularIntersection';
import { setGlobalMachineWireVerticesRef } from '../../../utils/sceneAction';
import { addActions, addBulk } from '../../../redux/simpleSupplySlice';
import { ComponentFactory } from '../../../factories/ComponentFactory';
import { FeederFactory } from '../../../factories/FeederFactory';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { computeBackwardProjection } from './previewUtils';
import parseColor from '../../../utils/parseColor';

/**
 * Main hook factory
 * @param {Object} params
 * @param {THREE.Scene} params.scene
 * @param {THREE.Camera} params.camera
 * @param {THREE.Renderer} params.renderer
 * @param {Array} params.snapPoints
 * @param {Object} params.options - {enabled, onWireEnd, onWireStart, onPreviewUpdate}
 * @param {Object} params.config - Configuration object
 * @param {string} params.config.commandType - COMMANDS enum value (e.g., COMMANDS.DRAW_SOCKET)
 * @param {number} params.config.wireColor - Wire color (e.g., 0x00ff00)
 * @param {Array} params.config.itemTypes - Available types (e.g., socketTypes, lightSwitchTypes)
 * @param {string} params.config.itemTypeName - Name for items (e.g., 'socket', 'lightSwitch')
 * @param {Function} params.config.createSymbol - (position, direction, angle, isPreview, itemType) => THREE.Object3D
 * @param {Function} params.config.createCommand - (action, componentData, feederData, sceneData) => BaseWiringCommand
 * @param {boolean} params.config.simplifyCollinearVertices - If true, removes collinear vertices (default: true). Set to false for spline curves.
 */
export const useBaseWiring = ({
    scene,
    camera,
    renderer,
    snapPoints,
    options = {},
    config
}) => {
    const {
        enabled = () => true,
        onWireEnd = () => {},
        onWireStart = () => {},
        onFirstPoint = () => {}, // ✅ Bell için: İlk nokta tıklandığında (panel çıkışı, trafo yerleştirme)
        onPreviewUpdate = () => {},
        onItemAdd = () => {}, // ✅ Callback for item addition
    } = options;

    const {
        commandType: targetCommandType,
        wireColor,
        itemTypes,
        itemTypeName,
        createSymbol,
        createCommand,
        createComponentData,  // Optional: custom component creation
        createFeederData,     // Optional: custom feeder creation
        simplifyCollinearVertices = true, // ✅ Default: true (polylines), false for splines
        disablePerpendicularExit = false, // ✅ Default: false (perpendicular exit enabled), true for mainPanel
    } = config;

 
    // ✅ Common refs
    const startRef = useRef(false);
    // Common refs used across wiring flows
    const verticesRef = useRef([]);
    const undoStackRef = useRef([]);
    const redoStackRef = useRef([]);
    const tempLineRef = useRef(null);
    const snapMarkerRef = useRef(null);
    const snapSourceRef = useRef(null);
    const previewSymbolRef = useRef(null);
    const previewMarkersRef = useRef([]);
    const wireMarkersRef = useRef([]);
    const finalPointRef = useRef(null);
    const previewEndPointRef = useRef(null);
    const lastMouse = useRef(null);
    const offsetLineRef = useRef(null);
    const intersectedLineRef = useRef(null);
    const flipDirectionRef = useRef(1);
    const itemsRef = useRef([]);
    const itemCountRef = useRef(0);
    const itemTypeIndexRef = useRef(0);
    const panelExitStateSnapshotRef = useRef(null); // ✅ Panel exit durumunu kaydetmek için
    const itemTypeRef = useRef(null);
    const backwardActiveRef = useRef(false);
    const backwardSideRef = useRef(0);
    const ctrlPressedRef = useRef(false); // ✅ CTRL tuşu kontrolü için
    const angleModeRef = useRef(false); // ✅ Açı modu (toggle)
    const midpointSnapActiveRef = useRef(false); // ✅ Midpoint snap modu (Ctrl+G basılı iken)
    const ctrlUsedForSnapRef = useRef(false); // ✅ Ctrl tuşunun snap için kullanılıp kullanılmadığını takip et
    const autoFinishInProgressRef = useRef(false); // ✅ Auto-finish işlemi devam ediyor mu?
    const lastOffsetDirectionRef = useRef(null); // ✅ Son tıklanan açılı offset çizgisinin yönü (paralel çizim için)

    // ✅ Common state
    const [step, setStep] = useState(0);
    const [flipDirection, _setFlipDirection] = useState(1);

    // ✅ Redux
    const dispatch = useDispatch();
    const { commandType } = useSelector((state) => state.operation);
    const gridMode = useSelector((state) => state.mods.gridMode);
    const gridStep = useSelector((state) => state.mods.gridStep);
    const objectSnap = useSelector((state) => state.mods.objectSnap);
    const operationData = useSelector((state) => state.operation.data);
    const snapMode = useSelector((state) => state.mods.snapMode);
    const orthoMode = useSelector((state) => state.mods.orthoMode);
    const floorPlans = useSelector(selectAllFloorPlans);
    const wires = useSelector(selectAllWires);
    const settings = useSelector((state) => state.settings);

    // ✅ Config
    const snapWall = true;
    const snapState = useRef({ x: 0, y: 0 });
    const addPosition = operationData?.addPosition || 'start';
    const movementOffset = 5;

    const setFlipDirection = (valOrUpdater) => {
        const newValue = typeof valOrUpdater === 'function' ? valOrUpdater(flipDirectionRef.current) : valOrUpdater;
        flipDirectionRef.current = newValue;
        _setFlipDirection(newValue);
    };

    // ✅ Initialize itemType from operationData.initialItemType or defaultFixtureType (sadece başlangıçta)
    const lastInitialTypeRef = useRef(null);
    useEffect(() => {
        setGlobalMachineWireVerticesRef(verticesRef);

        // ✅ initialItemType, defaultFixtureType veya defaultSwitchType değiştiyse (yeni komut) set et
        // Aynı type ise (mouse move, vb) set etme ('t' tuşu değişikliklerini koru)
        if (itemTypes.length > 0) {
            // ✅ Önce initialItemType, sonra defaultFixtureType, sonra defaultSwitchType kontrol et
            const initialType = operationData?.initialItemType ||
                               operationData?.defaultFixtureType ||
                               operationData?.defaultSwitchType;

            // ✅ initialType değiştiyse veya ilk kez set ediyorsa
            if (initialType && initialType !== lastInitialTypeRef.current) {
                const foundType = itemTypes.find(t => t.type === initialType || t.name === initialType);
                if (foundType) {
                    itemTypeRef.current = foundType;
                    lastInitialTypeRef.current = initialType;
                } else {
                    if (!itemTypeRef.current) {
                        itemTypeRef.current = itemTypes[0];
                    }
                }
            } else if (!initialType && !itemTypeRef.current) {
                // initialType yoksa ve itemTypeRef boşsa, ilk tipi kullan
                itemTypeRef.current = itemTypes[0];
            }
        }
    }, [operationData?.initialItemType, operationData?.defaultFixtureType, operationData?.defaultSwitchType, itemTypes]); // ✅ type değiştiğinde

    // ✅ Global key listeners for modifier keys (ayrı useEffect)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Control') {
                ctrlPressedRef.current = true;
            }
            // Toggle angle mode on Ctrl + <
            if (e.key === '<' && (e.ctrlKey || e.metaKey)) {
                angleModeRef.current = !angleModeRef.current;
            }

            // ✅ Ctrl+G basılı iken midpoint snap aktif
            if (e.key === 'g' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                midpointSnapActiveRef.current = true;
            }

        };
        const handleKeyUp = (e) => {
            if (e.key === 'Control') ctrlPressedRef.current = false;

            // ✅ G veya Ctrl bırakıldığında midpoint snap pasif
            if (e.key === 'g' || e.key === 'Control') {
                midpointSnapActiveRef.current = false;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []); // ✅ Sadece mount/unmount'da

    /**
     * ✅ Panel'den başlatma
     */
    useEffect(() => {
 
        const hasRestoreVertices = Array.isArray(operationData?.restoreVertices) && operationData.restoreVertices.length > 0;

        // Allow panel-start to run when we are restoring a previous drawing
        // even if enabled() hasn't returned true yet (race condition between
        // setCommandType and local enabled flags). For normal flow enabled()
        // must be true.
        if (
            commandType !== targetCommandType ||
            !operationData?.initialPoint ||
            !operationData?.source ||
            (!enabled() && !hasRestoreVertices) ||
            startRef.current
        ) {
            return;
        }

        const panelID = operationData.source.panelID;
        let panel = null;
        scene.traverse(obj => {
            if (obj.userData?.id === panelID) {
                panel = obj;
            }
        });

        const point = new THREE.Vector3(
            operationData.initialPoint.x,
            operationData.initialPoint.y,
            0
        );

        if (panel) {
            // ✅ Panel exit state snapshot'ı kaydet (değişiklikten ÖNCE)
            panelExitStateSnapshotRef.current = savePanelExitState(scene, panel);

            // ✅ UNDO modunda (isRedo === false) exit point EKLEME
            // Normal UI akışı (isRedo undefined) ve REDO (isRedo === true) için ekle
            const isUndoMode = operationData.isRedo === false;

            if (!isUndoMode) {
                // Normal akış veya REDO: yeni exit point ekle (wire'lar otomatik ötelenir)
                const currentExitCount = calculatePanelExitCount(panel);
                const newExitCount = currentExitCount + 1;
                updatePanelExitPoints(panel, newExitCount, scene, addPosition);
            }

            const exitPoints = findPanelExitPoints(panel);
            if (exitPoints.length > 0) {
                const startExitPoint = addPosition === 'end'
                    ? exitPoints[0]
                    : exitPoints[exitPoints.length - 1];

                const realWorldPosition = new THREE.Vector3();
                startExitPoint.getWorldPosition(realWorldPosition);
                point.copy(realWorldPosition);
            }
        }

        const snapSource = {
            type: 'panelConnection',
            position: point.clone(),
            p: {
                type: operationData.source.type,
                no: operationData.source.no,
                outNo: operationData.source.outNo,
                UUID: operationData.source.UUID,
                OutID: operationData.source.OutID,
                panelID: operationData.source.panelID
            },
            userData: {
                type: 'panelConnection',
                sourceObject: getObjectById(scene, operationData.source.panelID),
            },
        };

    const marker = createSnapMarker(8, snapSource);
        marker.position.copy(point);
        scene.add(marker);
        snapMarkerRef.current = marker;

     verticesRef.current.push(point);
    startRef.current = true;
        snapSourceRef.current = snapSource;

        // ✅ Bell için: İlk nokta callback (trafo yerleştirme)
        // Callback'e verticesRef geçir ki trafo output point ile güncelleyebilsin
        if (onFirstPoint) {
            onFirstPoint(point, snapSource, verticesRef);
        }

        dispatch(setCommandMessage("Sonraki noktaları seçin. Bitirmek için Enter/Space, iptal için ESC."));
        setStep(1);
    }, [operationData, enabled, scene, commandType, targetCommandType, dispatch, addPosition]);

    /**
     * ✅ Start from initialPoint (Generic - e.g. from SocketTypeMenu)
     */
    useEffect(() => {
        const hasRestoreVertices = Array.isArray(operationData?.restoreVertices) && operationData.restoreVertices.length > 0;

        if (
            commandType !== targetCommandType ||
            !operationData?.initialPoint ||
            operationData?.source || // Skip if source is present (handled by panel effect)
            (!enabled() && !hasRestoreVertices) ||
            startRef.current
        ) {
            return;
        }

        const point = new THREE.Vector3(
            operationData.initialPoint.x,
            operationData.initialPoint.y,
            0
        );

        // Create a generic snap source
        const snapSource = {
            type: 'initialPoint',
            position: point.clone(),
            userData: {
                type: 'initialPoint'
            }
        };

        const marker = createSnapMarker(8, snapSource);
        marker.position.copy(point);
        scene.add(marker);
        snapMarkerRef.current = marker;

        verticesRef.current.push(point);
        startRef.current = true;
        snapSourceRef.current = snapSource;

        // ✅ Bell için: İlk nokta callback (trafo yerleştirme)
        // Callback'e verticesRef geçir ki trafo output point ile güncelleyebilsin
        if (onFirstPoint) {
            onFirstPoint(point, snapSource, verticesRef);
        }

        dispatch(setCommandMessage("Sonraki noktaları seçin. Bitirmek için Enter/Space, iptal için ESC."));
        setStep(1);

    }, [operationData, enabled, scene, commandType, targetCommandType, dispatch]);

    /**
     * ✅ Vertex güncelleme helper
     */
    const pushOrUpdateVertex = (newPos, mouseWorldPoint) => {
        const count = verticesRef.current.length;

        // If provided mouseWorldPoint is not set, derive from lastMouse and camera
        if (!mouseWorldPoint && lastMouse.current && camera) {
            mouseWorldPoint = new THREE.Vector3(lastMouse.current.x, lastMouse.current.y, 0).unproject(camera);
        }

        // Check backward offset projection first (spline hatlarında devre dışı)
        const backward = simplifyCollinearVertices && computeBackwardProjection(
            verticesRef.current,
            newPos,
            mouseWorldPoint,
            backwardActiveRef,
            backwardSideRef,
            {
                offset: movementOffset,
                ctrlPressed: angleModeRef.current // ✅ Toggle modu
            }
        );
        if (backward) {
            
            verticesRef.current.push(backward.offsetPoint);
            verticesRef.current.push(backward.projectedPoint);
            return;
        }

        const countNow = verticesRef.current.length;
        if (countNow < 2) {
            verticesRef.current.push(newPos);
            return;
        }

        const p2 = verticesRef.current[countNow - 1];
        const p1 = verticesRef.current[countNow - 2];
        const dirPrev = new THREE.Vector3().subVectors(p2, p1).normalize();
        const dirNew = new THREE.Vector3().subVectors(newPos, p2).normalize();
        const angleDiff = dirPrev.angleTo(dirNew);
        const EPSILON = 0.0001;

        // If the new segment is (nearly) colinear with the previous one
        // we used to replace the last vertex to avoid redundant colinear
        // vertices. That optimization breaks local undo/redo ordering
        // (it silently mutates an already-recorded vertex). To keep
        // undo semantics correct we append the new vertex instead of
        // replacing the previous one. This may introduce redundant
        // colinear vertices but preserves predictable undo behavior.
        // ✅ ALWAYS append (no replace) - simplify only happens in confirmPlacement
        verticesRef.current.push(newPos);
    };

    /**
     * ✅ Cancel wiring
     */
    const cancelWiring = (fromLocalUndo = false) => {
        // ✅ UNDO MODUNDA: Local undo tamamlandığında panelExitManager'ı çağır
        // operationData.isRedo === false ise UNDO modundayız (restoreCallback'ten geldi)
        // fromLocalUndo=true → Ctrl+Z ile TAM local undo (stack boşaldı). Bu durumda
        // hattı GERİ GETİRME (re-execute yok) — aşağıdaki Esc-iptal mantığı atlanır.
        const isUndoMode = operationData?.isRedo === false;

        // UNDO modunda panel'i operationData.source.panelID'den al
        let undoModePanel = null;

        // panelID'yi farklı yerlerden almayı dene
        const panelID = operationData?.source?.panelID ||
                        operationData?.source?.p?.panelID ||
                        snapSourceRef.current?.p?.panelID ||
                        snapSourceRef.current?.userData?.sourceObject?.userData?.id;

        if (isUndoMode && panelID) {
            scene.traverse(obj => {
                if (obj.userData?.id === panelID) {
                    undoModePanel = obj;
                }
            });
        }

        // UNDO modunda Esc: kullanıcı undo'yu iptal ediyor → "sanki Ctrl+Z hiç
        // basılmamış gibi" davran:
        //  1. Wire'ı geri getir (en son undo edilen tx'i sync olarak yeniden execute
        //     et — restoreCallback bypass, local mode girilmeden).
        //  2. Manager position'ı geri al (next Ctrl+Z aynı tx'i tekrar undo etsin).
        //  3. Panel state'ine dokunma (outs / exit point / snapshot restore HEPSİ
        //     atlanır — global undo deferred bıraktığı için zaten korunmuş durumda).
        if (isUndoMode && fromLocalUndo) {
            // ✅ Ctrl+Z ile TAM local undo: kullanıcı hattı tamamen geri alıyor.
            // Esc-iptal mantığını (undone tx'i yeniden execute + mgr.position++) ATLA —
            // aksi halde hat (switch+buat+marker) yeniden yaratılıp aktif history'ye
            // geri konuyordu (leftover) ve sonraki global undo aynı hattı tekrar
            // çiziyordu (loop). Sadece aşağıdaki SceneCleanupManager temizliği çalışır.
            panelExitStateSnapshotRef.current = null;
        } else if (isUndoMode) {
            try {
                const mgr = (typeof window !== 'undefined') ? window.undoRedoManager : null;
                if (mgr && Array.isArray(mgr.history)) {
                    const nextPos = mgr.position + 1;
                    const undoneTx = mgr.history[nextPos];
                    if (undoneTx && Array.isArray(undoneTx.commands)) {
                        undoneTx.commands.forEach(cmd => {
                            if (cmd && typeof cmd.execute === 'function') {
                                try { cmd.execute(scene, dispatch); } catch (e) { /* command execute hatası */ }
                            }
                        });
                        mgr.position = nextPos;
                    }
                }
                // Wire sahneye yeniden eklendi — panel exit point + wire-edge sync
                // garantilemek için panelExitManager'ı tetikle. cmd.execute panel'in
                // bu fonksiyonlarını çağırmıyor → exit point ile wire ilk kenarı
                // arasındaki hizalama bozuk kalırdı.
                if (panelID) {
                    let panelObj = null;
                    scene.traverse(obj => { if (obj.userData?.id === panelID) panelObj = obj; });
                    if (panelObj) {
                        const exitCount = Array.isArray(panelObj.userData?.outs)
                            ? panelObj.userData.outs.length
                            : calculatePanelExitCount(panelObj);
                        try { updatePanelExitPoints(panelObj, exitCount, scene); } catch (e) { /* ignore */ }
                        if (exitCount > 0) {
                            try { updatePanelWiringPositions(scene, panelObj); } catch (e) { /* ignore */ }
                        }
                    }
                }
            } catch (e) {
                // undo iptali wire restore başarısız — devam et, panel state korunmuş kalır
            }
            panelExitStateSnapshotRef.current = null;
        } else {
            // ✅ Panel çıkış durumunu geri yükle (snapshot kullanarak)
            if (snapSourceRef.current?.type === 'panelConnection') {
                const sourcePanel = snapSourceRef.current.userData?.sourceObject;
                if (sourcePanel && panelExitStateSnapshotRef.current) {
                    // Snapshot varsa geri yükle
                    restorePanelExitState(scene, sourcePanel, panelExitStateSnapshotRef.current);
                    panelExitStateSnapshotRef.current = null; // Snapshot'ı temizle
                } else if (sourcePanel) {
                    // Fallback: snapshot yoksa eski yöntemi kullan
                    removeLastPanelExit(scene, sourcePanel, addPosition);
                }
            }
        }

        // ✅ Undo tüm eklenen item'leri (socket, fixture, etc.)
        // skipRedux=true çünkü item'ler Enter'a basılmadan önce Redux'a eklenmedi
        undoStackRef.current.forEach(action => {
            if (action.type === itemTypeName) {
                if (action.command) {
                    action.command.undo(scene, dispatch, true);
                } else if (action._sceneObj) {
                    scene.remove(action._sceneObj);
                    action._sceneObj.traverse(child => {
                        if (child.geometry) child.geometry.dispose();
                        if (child.material) child.material.dispose();
                    });
                }
            }
        });

        SceneCleanupManager.cleanupWiringSession(
            {
                tempLineRef,
                snapMarkerRef,
                previewSymbolRef,
                previewMarkersRef,
                wireMarkersRef,
                offsetLineRef,
                intersectedLineRef,
                verticesRef,
                startRef,
                snapSourceRef,
                undoStackRef,
                redoStackRef,
                itemsRef,
                itemCountRef,
            },
            scene
        );

                dispatch(resetOperation());
                // Clear any selection so objects return to their original visual state
                dispatch(clearSelection());
                dispatch(setCommandMessage(""));

                // ✅ onWireCancel callback'i çağır (bellTrafo gibi özel temizlik için)
                if (options.onWireCancel && typeof options.onWireCancel === 'function') {
                    options.onWireCancel();
                }
    };

    /**
     * ✅ Temp line güncelleme
     */
    // updateTempLine optionally accepts a preview point and mouseWorldPoint to draw the backward-offset preview
    const updateTempLine = (previewPoint, mouseWorldPoint) => {
        let pointsToDraw = verticesRef.current;
        if (previewPoint) {
            if (!mouseWorldPoint && lastMouse.current && camera) {
                mouseWorldPoint = new THREE.Vector3(lastMouse.current.x, lastMouse.current.y, 0).unproject(camera);
            }
            const backwardPreview = simplifyCollinearVertices && computeBackwardProjection(
                verticesRef.current,
                previewPoint,
                mouseWorldPoint,
                backwardActiveRef,
                backwardSideRef,
                {
                    offset: movementOffset,
                    ctrlPressed: angleModeRef.current // ✅ Toggle modu
                }
            );
            if (backwardPreview) {
                pointsToDraw = [...verticesRef.current, backwardPreview.offsetPoint, backwardPreview.projectedPoint];
                // ensure previewEndPointRef reflects the actual previewed end (projected point)
                previewEndPointRef.current = backwardPreview.projectedPoint.clone ? backwardPreview.projectedPoint.clone() : backwardPreview.projectedPoint;
            } else {
                pointsToDraw = [...verticesRef.current, previewPoint];
                previewEndPointRef.current = previewPoint;
            }
        }

        if (pointsToDraw.length > 1) {
            // Determine preview color from new nested settings structure.
            // Map some legacy itemTypeName values to the new keys.
            const typeMap = {
                socket: 'socket',
                machine: 'machine',
                bell: 'fixture',
                lighting: 'lightingSwitch',
                fixture: 'fixture'
            };
            const settingsKey = typeMap[itemTypeName] || itemTypeName;
            const previewColorSetting = settings?.[settingsKey]?.wirePreviewColor ?? wireColor;
            const parsedPreview = parseColor(previewColorSetting);

            // ✅ Spline preview için CatmullRomCurve3 kullan (fixture için).
            // İSTİSNA: L tuşu ile "düz mod" açıksa preview de düz (polyline) kalır —
            // kalıcı hat ile birebir tutarlı (WYSIWYG). fixtureLineModeRef paylaşımlı.
            let previewPoints = pointsToDraw;
            if (simplifyCollinearVertices === false && !fixtureLineModeRef.current && pointsToDraw.length >= 2) {
                const curve = new THREE.CatmullRomCurve3(pointsToDraw);
                const divisions = Math.max(10, pointsToDraw.length * 10);
                previewPoints = curve.getPoints(divisions);
            }

            if (tempLineRef.current) {
                const geometry = new THREE.BufferGeometry().setFromPoints(previewPoints);
                tempLineRef.current.geometry.dispose();
                tempLineRef.current.geometry = geometry;
                // try to update material for preview visibility
                    if (tempLineRef.current.material) {
                    tempLineRef.current.material.color.set(parsedPreview.colorNumber);
                    tempLineRef.current.material.linewidth = 4;
                    tempLineRef.current.material.transparent = parsedPreview.alpha < 1;
                    tempLineRef.current.material.opacity = parsedPreview.alpha;
                    tempLineRef.current.material.depthTest = false;
                    tempLineRef.current.material.needsUpdate = true;
                }
            } else {
                const geometry = new THREE.BufferGeometry().setFromPoints(previewPoints);
                const material = new THREE.LineBasicMaterial({ color: parsedPreview.colorNumber, linewidth: 4, transparent: parsedPreview.alpha < 1, opacity: parsedPreview.alpha, depthTest: false });
                tempLineRef.current = new THREE.Line(geometry, material);
                tempLineRef.current.name = `temp${itemTypeName}WirePreview`;
                scene.add(tempLineRef.current);
            }
        } else if (tempLineRef.current) {
            scene.remove(tempLineRef.current);
            tempLineRef.current.geometry.dispose();
            tempLineRef.current.material.dispose();
            tempLineRef.current = null;
        }
    };

    /**
     * ✅ Add item (socket, lightSwitch, fixture, machine, etc.)
     * @param {number} rotateValue - Rotation offset in degrees (from R key)
     */
    const addItem = (rotateValue = 0) => {
        if (verticesRef.current.length < 1 || !previewEndPointRef.current) {
              return;
        }

        // ✅ Vertex snapshot: undo sırasında verticesRef'i bu sayıya truncate eder
        const vertexCountBefore = verticesRef.current.length;

        // If backward-offset preview is active, compute and add both the perpendicular
        // corner (offsetPoint) and the projected point so the permanent wire matches preview.
        let mouseWorld = null;
        if (lastMouse.current && camera) {
            mouseWorld = new THREE.Vector3(lastMouse.current.x, lastMouse.current.y, 0).unproject(camera);
        }

        const backwardPreview = simplifyCollinearVertices && computeBackwardProjection(
            verticesRef.current,
            previewEndPointRef.current,
            mouseWorld,
            backwardActiveRef,
            backwardSideRef,
            {
                offset: movementOffset,
                ctrlPressed: angleModeRef.current // ✅ Toggle modu
            }
        );
        let itemVertex = null;
        let verticesAdded = 0; // ✅ Track how many vertices were added for undo
        if (backwardPreview) {
            
            // push perpendicular corner then projected point
            verticesRef.current.push(backwardPreview.offsetPoint.clone ? backwardPreview.offsetPoint.clone() : backwardPreview.offsetPoint);
            verticesRef.current.push(backwardPreview.projectedPoint.clone ? backwardPreview.projectedPoint.clone() : backwardPreview.projectedPoint);
            itemVertex = verticesRef.current[verticesRef.current.length - 1].clone();
            verticesAdded = 2; // ✅ Two vertices added
        } else {
            itemVertex = previewEndPointRef.current.clone();
            verticesRef.current.push(itemVertex);
            verticesAdded = 1; // ✅ One vertex added
        }

        const p1 = verticesRef.current.at(-2);
        const p2 = verticesRef.current.at(-1);
        const itemPower = itemTypeRef.current.power;
        const direction = new THREE.Vector3().subVectors(p2, p1);
        const normalizedDirection = direction.clone().normalize(); // ✅ Preview ile aynı: normalized direction
        const angle = Math.atan2(direction.y, direction.x);

        // ✅ Machine için özel offset uygulaması (50x50 dörtgen, base point ortada)
        let itemPosition = p2.clone();
        if (config.itemOffset) {
            const normalizedDirection = direction.clone().normalize();
            itemPosition.add(normalizedDirection.multiplyScalar(config.itemOffset));
        }

        const componentId = generateUniqueId(itemTypeName);
        const sourcePanelId = snapSourceRef.current?.p?.panelID;

        // Component oluştur (custom veya default)
        // ✅ BellButton gibi componentler için createComponentData null olabilir (component sadece handleWireEnd'de oluşturulur)
        const newComponent = config.createComponentData === null
            ? null
            : (createComponentData
                ? createComponentData(itemPosition, angle, itemPower, itemTypeRef.current, componentId)
                : ComponentFactory.createSocket(itemPower, {
                    id: componentId,
                    name: itemTypeRef.current.type,
                    position: { x: itemPosition.x, y: itemPosition.y, z: 0 },
                    rotation: { x: 0, y: 0, z: angle },
                    electrical: { installedPower: itemPower, phaseCount: 1 }
                }));

        // Feeder oluştur (custom veya default)
        // ✅ BellButton gibi componentler için createFeederData null olabilir (feeder sadece handleWireEnd'de oluşturulur)
        const itemVertices = [p1, p2].map(v => ({ x: v.x, y: v.y, z: v.z }));
        const cableLength = p1.distanceTo(p2);

        const newFeeder = config.createFeederData === null
            ? null
            : (createFeederData
                ? createFeederData(p1, p2, itemPower, itemTypeRef.current, componentId, sourcePanelId)
                : FeederFactory.createFeederForLoad(itemTypeName, itemPower, sourcePanelId, componentId, {
                    cable: { length: Math.round(cableLength / 10), route: itemVertices },
                    electrical: { phaseCount: 1, voltage: 220 },
                    vertices: itemVertices
                }));

        // ✅ Machine için createCommand null olabilir (command sadece handleWireEnd'de oluşturulur)
        if (createCommand && !config.skipItemExecution) {
            // Command oluştur
            const itemCommand = createCommand('ADD', newComponent, newFeeder, {
                [`${itemTypeName}Position`]: [itemPosition.x, itemPosition.y, 0], // ✅ Offset uygulanmış pozisyon
                rotation: angle,
                dir: flipDirectionRef.current,
                color: wireColor,
                [`${itemTypeName}Type`]: itemTypeRef.current.type,
                tempLineUUID: null,
                wireVertices: itemVertices,
                rotateValue: rotateValue // ✅ R tuşu ile eklenen rotasyon
            });

            // Execute (skipRedux=true - Enter'da bulk eklenecek)
            itemCommand.execute(scene, dispatch, true);

            // Undo stack'e ekle (sadece execute edilen itemlar için)
            undoStackRef.current.push({
                type: itemTypeName,
                command: itemCommand,
                vertex: itemVertex,
                verticesAdded: verticesAdded,
                vertexCountBefore: vertexCountBefore, // ✅ Snapshot: undo truncate hedefi
                itemData: {
                    position: itemPosition.clone(), // ✅ Offset uygulanmış pozisyon
                    type: itemTypeRef.current.type,
                    direction: normalizedDirection, // ✅ Normalized direction (preview ile aynı)
                    power: itemPower,
                    uuid: componentId,
                    id: componentId, // ✅ Add id for compatibility
                    rotateValue: rotateValue // ✅ R tuşu ile eklenen rotasyon
                }
            });
        }

        redoStackRef.current = [];

        const newItem = {
            position: itemPosition.clone(), // ✅ Offset uygulanmış pozisyon
            type: itemTypeRef.current.type,
            direction: normalizedDirection, // ✅ Normalized direction (preview ile aynı)
            power: itemPower,
            uuid: componentId,
            id: componentId, // ✅ Add id for compatibility
            rotateValue: rotateValue // ✅ R tuşu ile eklenen rotasyon
        };

        // createCommand yoksa (fixture gibi) sembolü doğrudan sahneye ekle
        // skipItemExecution olan tipler (mainPanel, electricalPanel, machine, bell)
        // kendi sembollerini handleWireEnd'de oluşturur — burada oluşturma.
        if (!createCommand && createSymbol && scene && !config.skipItemExecution) {
            const symbol = createSymbol(itemPosition, normalizedDirection, angle, false, itemTypeRef.current, { rotateValue });
            if (symbol) {
                scene.add(symbol);
                newItem._sceneObj = symbol;

                // Undo stack'e ekle (Ctrl+Z ile geri alınabilsin)
                undoStackRef.current.push({
                    type: itemTypeName,
                    vertex: itemVertex,
                    verticesAdded: verticesAdded,
                    vertexCountBefore: vertexCountBefore, // ✅ Snapshot: undo truncate hedefi
                    _sceneObj: symbol
                });
            }
        }

        itemsRef.current.push(newItem);

        // ✅ Notify callback
        if (onItemAdd) {
            onItemAdd(newItem);
        }

        itemCountRef.current++;
    };

    // Return refs and helpers for event handlers
    return {
        // Refs
        startRef,
        verticesRef,
        undoStackRef,
        redoStackRef,
        tempLineRef,
        snapMarkerRef,
        snapSourceRef,
        previewSymbolRef,
        wireMarkersRef,
        previewMarkersRef,
        finalPointRef,
        previewEndPointRef,
        lastMouse,
        offsetLineRef,
        intersectedLineRef,
        flipDirectionRef,
        itemsRef,
        itemCountRef,
        itemTypeIndexRef,
        itemTypeRef,
        ctrlPressedRef, // ✅ CTRL tuşu ref'i dışarı aktar
        angleModeRef, // ✅ Açı modu ref'i dışarı aktar
        midpointSnapActiveRef, // ✅ Midpoint snap ref'i dışarı aktar
        autoFinishInProgressRef, // ✅ Auto-finish durumu ref'i dışarı aktar
        lastOffsetDirectionRef, // ✅ Son açılı offset çizgisinin yönü

        // State
        step,
        setStep,
        flipDirection,
        setFlipDirection,

        // Redux
        dispatch,
        commandType,
        gridMode,
        gridStep,
        objectSnap,
        operationData,
        snapMode,
        orthoMode,
        floorPlans,
        wires,

        // Config
        snapWall,
        snapState,
        addPosition,
        movementOffset,
        wireColor,
        itemTypes,
        itemTypeName,
        targetCommandType,
        simplifyCollinearVertices,
        disablePerpendicularExit,

        // Helpers
        pushOrUpdateVertex,
        cancelWiring,
        updateTempLine,
        addItem,
        createSymbol,
        createCommand,
        enabled,
        onWireEnd,
        onWireStart,
        onPreviewUpdate,
        scene,
        camera,
        renderer,
        snapPoints,
    };
};

export default useBaseWiring;
